import TransactionInterface from "@homegame/common/dist/interface/transaction.interface";
import TransactionType from "@homegame/common/dist/enum/transaction-type.enum";
import { Box, Card, CardContent, Stack } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import Typography from "@mui/material/Typography";
import PaymentIcon from "@mui/icons-material/Payment";
import formatCurrency from "../../core/helpers/format-currency.helper";
import { useAuth } from "../../core/context/auth.context";
import { FC } from "react";
import { getTransactionText } from "../../core/helpers/get-transaction-text.helper";

type CardTransactionType = "win" | "loss" | "payment" | "return";

interface Props {
  item: DebtHistory;
}

export interface DebtHistory extends TransactionInterface {
  originalTransactionId: number;
  viewerId: number;
  peerId: number;
  amountLost: number;
  amountWon: number;
  runningTotal: number;
}

const getCardBackground = (type: CardTransactionType): string => {
  switch (type) {
    case "win":
      return "linear-gradient(135deg, rgba(63, 227, 126, 0.10), rgba(63, 227, 126, 0.01))";

    case "loss":
      return "linear-gradient(135deg, rgba(255, 92, 92, 0.10), rgba(255, 92, 92, 0.01))";

    case "payment":
      return "linear-gradient(135deg, rgba(33, 150, 243, 0.10), rgba(33, 150, 243, 0.01))";

    case "return":
      return "linear-gradient(135deg, rgba(156, 39, 176, 0.10), rgba(156, 39, 176, 0.01))";

    default:
      return "linear-gradient(135deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.03))";
  }
};

const getIcon = (type: CardTransactionType) => {
  switch (type) {
    case "win":
      return <ArrowUpwardIcon sx={{ color: "#4caf50" }} />;
    case "loss":
      return <ArrowDownwardIcon sx={{ color: "#f44336" }} />;
    case "payment":
      return <PaymentIcon sx={{ color: "#2196f3" }} />;
    case "return":
      return <CheckCircleIcon sx={{ color: "#9c27b0" }} />;
  }
};

const getAmountColor = (type: CardTransactionType) => {
  switch (type) {
    case "win":
      return "success.main";
    case "loss":
      return "error";
    case "payment":
      return "info.main";
    default:
      return "#fff";
  }
};

const getAmount = (type: CardTransactionType, item: DebtHistory) => {
  const amount = item.amountWon || item.amountLost || 0;

  const formattedAmount = formatCurrency(amount, item.currency);
  switch (type) {
    case "win":
      return `+${formattedAmount}`;
    case "loss":
      return `-${formattedAmount}`;
    default:
      return formattedAmount;
  }
};

export const DebtItemComponent: FC<Props> = ({ item }) => {
  const { user } = useAuth();

  const title = getTransactionText(item, user?.id);
  let type: CardTransactionType = "payment";
  if (item.amountWon > 0) type = "win";
  if (item.amountLost > 0) type = "loss";
  if (item.type === TransactionType.PAYMENT) type = "payment";

  return (
    <Card
      sx={{
        background: getCardBackground(type),
        backdropFilter: "blur(10px)",
        borderRadius: 3,
        border: "1px solid rgba(255,255,255,0.1)",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        mb: 1.5,
        px: 2,
      }}
    >
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box lineHeight="14px">{getIcon(type)}</Box>
          <Box flex={1}>
            <Typography fontWeight={400} fontSize="16px">
              {title}
            </Typography>
          </Box>
          <Box textAlign="right">
            <Typography fontWeight="bold" color={getAmountColor(type)}>
              {getAmount(type, item)}
            </Typography>
            <Typography variant="body2" color="#fff" fontSize="14px">
              {formatCurrency(item.runningTotal, item.currency)}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};
